import { useEffect, useRef } from "react";

/**
 * Syncs a top phantom scrollbar with the actual table container scrollbar,
 * giving users a scrollbar at the top of wide tables without needing to
 * scroll to the bottom to find it.
 *
 * Returns refs to attach to: the top scrollbar div, its inner sizing div,
 * and the scrollable table container.
 *
 * @param dependency - Re-runs the width calculation when this value changes
 *                     (pass the data array so widths recalculate after load).
 */
export function useScrollbarSync(dependency: unknown) {
    const topScrollRef = useRef<HTMLDivElement>(null);
    const bottomScrollRef = useRef<HTMLDivElement>(null);
    const topScrollInnerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const topScroll = topScrollRef.current;
        const bottomScroll = bottomScrollRef.current;
        const topScrollInner = topScrollInnerRef.current;

        if (!topScroll || !bottomScroll || !topScrollInner) return;

        topScrollInner.style.width = `${bottomScroll.scrollWidth}px`;

        const syncFromTop = () => { bottomScroll.scrollLeft = topScroll.scrollLeft; };
        const syncFromBottom = () => { topScroll.scrollLeft = bottomScroll.scrollLeft; };

        topScroll.addEventListener("scroll", syncFromTop);
        bottomScroll.addEventListener("scroll", syncFromBottom);

        return () => {
            topScroll.removeEventListener("scroll", syncFromTop);
            bottomScroll.removeEventListener("scroll", syncFromBottom);
        };
    }, [dependency]);

    return { topScrollRef, bottomScrollRef, topScrollInnerRef };
}
